import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './Panel_DiseaseValueList.scss';
import { _className, EditableDBItemV3, EventType_Create, EventType_Update, EventType_UpsertAll, LL_H_C, LL_V_L, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, openContent, performAction, ProtoComponent, SimpleListAdapter, stopPropagation, TS_DropDown, TS_Table, TS_Tabs } from '@nu-art/thunderstorm-frontend/index';
import { _values, arrayToMap, BadImplementationException, cloneObj, compare, dbObjectToId, Filter, MUSTNeverHappenException, removeItemFromArray, sortArray } from '@nu-art/ts-common';
import { ConfigKeyFE_ModalityTagId, Dialog_BookEditor, DropDown_Values, HCSDropDowns, ModuleFE_MeasurementUnit, ModuleFE_Reference, ModuleFE_Tag, ModuleFE_Value, ModuleFE_Variable, NormalValuesTable, Panel_Header, QPageMessage, QRangeInput, QScrollWrapper, resolveReferenceDisplay, } from '@app/hcs-frontend';
import { resolveValueDisplay, ValType_IND, valueType_Enumerated, valueType_Range, valueType_StandardRange, valueType_SubEnumerated, valueType_SubTime, valueType_Time, valueType_Tuple, variableType_AttributeInstance, } from '@app/hcs-shared';
import { DBDef_DiseaseProfile, DBDef_DiseaseView, DBDef_MultiVar, defaultVPName_Imaging, getCleanDiseaseValue, indicativeOptions, LikelihoodDirection_vD } from '@app/dp-shared';
import { DropDown_Likelihood, DropDown_LikelihoodDirection, DropDown_Severity } from '../../../_enum.js';
import { GenericWarningDialog } from '@app/styles';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseValue, ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../../_entity.js';
const moreHeaders = ['source', 'indicative', 'likelihood', 'severity'];
export class Panel_DiseaseValueList extends ProtoComponent {
    static defaultProps = {
        keys: ['selected', 'filterDiseaseValues', 'diseaseValues_tabId'],
    };
    // ######################### Life Cycle #########################
    __onDiseaseProfileUpdated(...params) {
        const dp = params[1];
        if (dp?._id === this.state.diseaseProfile?._id)
            this.reDeriveState({ diseaseProfile: dp, isFirstLoad: undefined });
    }
    __onMultiVarUpdated(...params) {
        if (![EventType_Update, EventType_Create, EventType_UpsertAll].includes(params[0]))
            return;
        const newMV = Array.isArray(params[1]) ? params[1][0] : params[1];
        if (newMV._id === this.state.multiVar?.item._id) {
            //Re-derive because we have a bunch of stuff to calculate
            this.reDeriveState();
        }
    }
    deriveStateFromProps(nextProps, state) {
        const selected = this.getQueryParam('selected', {});
        state.diseaseProfile = ModuleFE_DiseaseProfile.cache.unique(selected[DBDef_DiseaseProfile.dbKey]);
        state.diseaseView = ModuleFE_DiseaseView.cache.unique(selected[DBDef_DiseaseView.dbKey]);
        state.selectedTabId = this.getQueryParam('diseaseValues_tabId');
        const selectedMVId = selected[DBDef_MultiVar.dbKey];
        const multiVar = ModuleFE_MultiVar.cache.unique(selectedMVId);
        if (selectedMVId && !multiVar)
            throw new MUSTNeverHappenException(`Could not get MV for id ${selectedMVId}`);
        state.multiVar = multiVar ? new EditableDBItemV3(multiVar, ModuleFE_MultiVar) : undefined;
        state.rowLoadingIds ??= [];
        state.diseaseValues ??= [];
        state.valuesMap = {};
        const allVariables = ModuleFE_Variable.cache.all();
        state.variableMap = arrayToMap(allVariables, dbObjectToId);
        if (!state.multiVar || !state.diseaseProfile || !state.diseaseView)
            return state;
        state.valuesMap = arrayToMap(ModuleFE_Value.cache.all(), dbObjectToId);
        //Get ref data
        state.relatedReferences = ModuleFE_Reference.cache.filter(item => state.multiVar.item.referenceIds.includes(item._id));
        const variableIds = state.multiVar.item.variables.map(item => item.variableId);
        state.diseaseValues = sortArray(ModuleFE_DiseaseValue.cache.filter(item => item.multiVarId === state.multiVar.item._id), ['__created']);
        //Push clean disease value in last spot
        const cleanValue = getCleanDiseaseValue(state.diseaseProfile._id, state.multiVar.item._id);
        state.diseaseValues.push(cleanValue);
        allVariables.filter(item => variableIds.includes(item._id)).forEach(variable => {
            state.valuesMap[variable._id] = state.valuesMap[variable.valueId];
        });
        if (state.isFirstLoad === undefined) {
            state.isFirstLoad = true;
        }
        state.measurementUnitsByIdMap = arrayToMap(ModuleFE_MeasurementUnit.cache.all(), dbObjectToId);
        return state;
    }
    // ######################### Logic #########################
    updateDiseaseValue = async (diseaseValue, title, successTitle) => {
        const identifier = diseaseValue._id || 'newDV';
        this.setState({ rowLoadingIds: [...this.state.rowLoadingIds, identifier] });
        await performAction(async () => {
            if (!diseaseValue.likelihoodDirection)
                diseaseValue.likelihoodDirection = LikelihoodDirection_vD;
            await ModuleFE_DiseaseValue.v1.upsert(diseaseValue).executeSync();
        }, { type: 'notification', notificationLabels: 'Upserting Disease Value' });
        this.reDeriveState({ rowLoadingIds: this.state.rowLoadingIds.filter(i => i !== identifier) });
    };
    onDiseaseValueSelected = async (diseaseValue, mvv, valueId) => {
        if (!valueId && !diseaseValue._id)
            return;
        const updatedDv = { ...diseaseValue };
        if (!valueId)
            delete updatedDv.values[mvv.variableId];
        else
            updatedDv.values[mvv.variableId] = valueId;
        await this.updateDiseaseValue(updatedDv, 'Updating Disease Value', 'Updated Disease Value');
    };
    onDiseaseValueSelectedNotInScope = (diseaseValue, mvv, variable, value) => {
        const message = `Value "${resolveValueDisplay(value)}" is out of scope for variable ${variable.name}.\nClicking confirm will also add it to the scope`;
        GenericWarningDialog.show({
            bodyContent: message,
            confirmationAction: async () => {
                //Add To Scope
                const parentValue = ModuleFE_Value.cache.unique(variable.valueId);
                if (!parentValue)
                    throw new MUSTNeverHappenException(`Could not find value for id ${variable.valueId}`);
                switch (parentValue.type) {
                    case valueType_SubTime:
                    case valueType_SubEnumerated:
                        parentValue.data.scope.push(value._id);
                        break;
                }
                await ModuleFE_Value.v1.upsert(parentValue).executeSync();
                //Update DV
                this.onDiseaseValueSelected(diseaseValue, mvv, value._id);
            }
        });
    };
    onDiseaseLikelihoodSelected = async (diseaseValue, likelihood) => {
        const updatedDv = { ...diseaseValue, likelihood };
        if (!diseaseValue._id && !likelihood)
            return;
        return this.updateDiseaseValue(updatedDv, 'Updating Likelihood', 'Updated Likelihood');
    };
    onDiseaseLikelihoodDirectionSelected = async (dv, likelihoodDirection) => {
        const updatedDv = { ...dv, likelihoodDirection };
        if (!updatedDv._id && !likelihoodDirection)
            return;
        return this.updateDiseaseValue(updatedDv, 'Updating Likelihood', 'Updated Likelihood');
    };
    onDiseaseLikelihoodRangeUpdated = async (diseaseValue, likelihood) => {
        let updatedDv = undefined;
        //If no range in an empty DV
        if (!likelihood && !diseaseValue._id)
            return;
        //If range wasn't changed
        if (compare(likelihood, diseaseValue.likelihoodRange))
            return;
        if (likelihood)
            updatedDv = { ...diseaseValue, likelihoodRange: likelihood };
        else {
            diseaseValue = { ...diseaseValue };
            this.logInfo(Object.getOwnPropertyDescriptor(diseaseValue, 'likelihoodRange'));
            // Object.defineProperty(diseaseValue, 'likelihoodRange', {
            // 	configurable: true, // This makes the property configurable
            // 	writable: true,     // Optional: Allows the value to be changed
            // 	enumerable: true    // Optional: Makes the property visible during enumeration (e.g., in for...in loop)
            // });
            delete diseaseValue.likelihoodRange;
        }
        return this.updateDiseaseValue(updatedDv || diseaseValue, 'Updating Likelihood Range', 'Updated Likelihood Range');
    };
    onDiseaseSeveritySelected = async (diseaseValue, severity) => {
        if (!diseaseValue._id && !severity)
            return;
        const updatedDv = { ...diseaseValue, severityFactor: severity };
        return this.updateDiseaseValue(updatedDv, 'Updating Severity', 'Updated Severity');
    };
    onIndicativeSelected = async (diseaseValue, indicative) => {
        if (!diseaseValue._id && !indicative)
            return;
        const updatedDv = cloneObj(diseaseValue);
        if (!indicative) {
            delete updatedDv.indicative;
            return this.updateDiseaseValue(updatedDv, 'Updating Indicative', 'Updated Indicative');
        }
        //Indicative is either -1 or 1. No ranges in between as of right now.
        if (indicative.value !== -1 && indicative.value !== 1)
            return;
        updatedDv.indicative = indicative.value;
        return this.updateDiseaseValue(updatedDv, 'Updating Indicative', 'Updated Indicative');
    };
    onSourceTagSelected = async (diseaseValue, tagId) => {
        if (!diseaseValue._id && !tagId)
            return;
        const updatedDV = cloneObj(diseaseValue);
        if (!tagId)
            delete updatedDV.sourceId;
        else
            updatedDV.sourceId = tagId;
        return this.updateDiseaseValue(updatedDV, 'Updating Source Tag', 'Source Tag Updated');
    };
    onReferenceSelect = async (ref) => {
        if (ref.type !== 'book') {
            this.state.multiVar.set('referenceIds', [...this.state.multiVar.item.referenceIds, ref._id]);
            await this.state.multiVar.save();
        }
        else {
            Dialog_BookEditor.copy(async (ref) => {
                this.state.multiVar.set('referenceIds', [...this.state.multiVar.item.referenceIds, ref._id]);
                await this.state.multiVar.save();
            }, ref);
        }
    };
    onReferenceRightClick = (e, ref) => {
        if (!ref)
            return;
        stopPropagation(e);
        const menuItems = [{
                label: 'Remove Reference',
                onClick: async () => {
                    const multiVar = this.state.multiVar;
                    multiVar.set('referenceIds', multiVar.item.referenceIds.filter(i => i !== ref._id));
                    await multiVar.save();
                }
            }];
        const rightClickMenu = {
            id: 'reference-menu',
            adapter: SimpleListAdapter(menuItems, renderer => _jsx("div", { className: 'node-data', children: renderer.item.label })),
            originPos: { y: e.clientY, x: e.clientX, },
            modalPos: { x: 0, y: 1 },
            onNodeClicked: (path, item) => {
                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                item.onClick?.();
            }
        };
        ModuleFE_MouseInteractivity.showMenu(rightClickMenu);
    };
    onDiseaseValueRightClick = (e, dv) => {
        if (!dv)
            return;
        const menuItems = [{
                label: 'Remove Disease Value',
                onClick: async () => {
                    const ids = [...this.state.rowLoadingIds, dv._id];
                    this.setState({ rowLoadingIds: ids });
                    await ModuleFE_DiseaseValue.v1.delete(dv).executeSync();
                    this.reDeriveState({
                        rowLoadingIds: ids.filter(i => i !== dv._id),
                        diseaseValues: this.state.diseaseValues.filter(i => i._id !== dv._id)
                    });
                }
            }];
        const rightClickMenu = {
            id: 'disease-value-menu',
            adapter: SimpleListAdapter(menuItems, renderer => _jsx("div", { className: 'node-data', children: renderer.item.label })),
            originPos: { y: e.clientY, x: e.clientX, },
            modalPos: { x: 0, y: 1 },
            onNodeClicked: (path, item) => {
                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                item.onClick?.();
            }
        };
        ModuleFE_MouseInteractivity.showMenu(rightClickMenu);
    };
    getMultiVarColumns = () => {
        const multiVar = this.state.multiVar;
        if (!multiVar?.item)
            return [];
        return multiVar.item.variables;
    };
    getSortedDiseaseValues = () => {
        const mvvToRender = this.getMultiVarColumns();
        return sortArray(this.state.diseaseValues, (dv => {
            const value = ModuleFE_Value.cache.unique(dv.values[mvvToRender[0].variableId]);
            if (!value)
                return '';
            switch (value.type) {
                case 'enum-element':
                    return value.data.value;
                case 'range':
                case 'sub-range':
                    return value.data.scope;
                default:
                    return '';
            }
        }));
    };
    // ######################### Render  #########################
    render() {
        if (!this.state.multiVar)
            return this.renderPlaceholder();
        return _jsxs(LL_V_L, { className: 'panel-disease-values-list', children: [this.renderHeader(), this.renderMain()] });
    }
    renderPlaceholder() {
        return _jsx("div", { className: 'tab-placeholder', children: "Select a variable" });
    }
    renderHeader() {
        return _jsx(Panel_Header, { title: {
                label: this.state.variableMap?.[this.state.multiVar?.item.variables[0].variableId || '']?.name || 'Values',
                className: 'header__title'
            }, qsearchId: 'disease-values-panel-search', config: { filter: this.state.filter }, onFilterChanged: (filterText) => this.setQueryParam('filterDiseaseValues', filterText) });
    }
    renderMain() {
        if (!this.state.multiVar)
            return _jsx(QPageMessage, { message: 'Select a multi var' });
        const tabs = [
            { title: 'Values', uid: 'values', content: this.renderValues() },
            { title: 'References', uid: 'references', content: this.renderReferences() },
        ];
        return _jsx(TS_Tabs, { tabs: tabs, selectedTabId: this.state.selectedTabId, onSelected: (tabId) => this.setQueryParam('diseaseValues_tabId', tabId) });
    }
    // ######################### Render - Values Tab #########################
    renderUnitsFromDbValue = (value) => {
        if (value?.type !== valueType_Range && value?.type !== valueType_StandardRange)
            return '';
        const unitId = value.data.unitsId;
        if (!unitId)
            return '';
        return ` (${this.state.measurementUnitsByIdMap[unitId].unitAsString})`;
    };
    renderNormalDistribution = (value) => {
        if (value?.type !== valueType_StandardRange)
            return '';
        return _jsx("span", { className: 'variable-info-tooltip-icon', ...openContent.tooltip.right('variable-info-tooltip', () => _jsx("div", { className: 'variable-info-tooltip', children: _jsx(NormalValuesTable, { editable: false, value: value }) }), { contentHoverDelay: 200 }), children: "i" });
    };
    renderTableHeader = (header) => {
        if (!this.state.multiVar)
            return '';
        //Not an id of a variable
        if (moreHeaders.includes(header))
            return _jsx("div", { className: 'node-data', children: _jsx("div", { className: 'variable-name', children: header }) });
        const index = header;
        const variable = this.state.variableMap[this.state.multiVar.item.variables[index].variableId];
        if (!variable)
            throw new BadImplementationException(`Can't find variable for id ${header}`);
        const variableName = variable.name.split('.');
        const value = this.state.valuesMap[variable.valueId];
        //No Split points
        if (variableName.length === 1)
            return _jsx("div", { className: 'node-data', children: _jsxs("div", { className: 'variable-name', children: [`${variableName[0]}${this.renderUnitsFromDbValue(value)}`, this.renderNormalDistribution(value)] }) });
        return _jsxs("div", { className: 'node-data', children: [_jsx("div", { className: 'attributee', children: variableName[0] }), _jsxs("div", { className: 'variable-name', children: [`${variableName[1]}${this.renderUnitsFromDbValue(value)}`, this.renderNormalDistribution(value)] })] });
    };
    renderValues() {
        const mvvToRender = this.getMultiVarColumns();
        let diseaseValueRows = this.getSortedDiseaseValues();
        if (this.state.filter) {
            const filter = new Filter(item => {
                const innerValueIds = _values(item.values);
                const enumElements = ModuleFE_Value.cache.filter(value => value.type === 'enum-element' && innerValueIds.includes(value._id));
                return [...enumElements.map(value => value.data.value)];
            });
            diseaseValueRows = filter.filterSort(diseaseValueRows, this.state.filter);
        }
        const cleanDiseaseValue = diseaseValueRows.find(dv => !dv._id);
        removeItemFromArray(diseaseValueRows, cleanDiseaseValue);
        if (cleanDiseaseValue)
            diseaseValueRows.push(cleanDiseaseValue);
        const extraHeaders = [...moreHeaders];
        if (this.state.diseaseView?.label !== defaultVPName_Imaging)
            extraHeaders.shift();
        return _jsx(QScrollWrapper, { children: _jsx(TS_Table, { header: [
                    ...mvvToRender.map((header, i) => ({ header: `${i}`, widthPx: 200 })),
                    ...extraHeaders.map(header => ({ header, widthPx: (header === 'likelihood' ? 240 : 100) }))
                ], headerRenderer: this.renderTableHeader, rows: diseaseValueRows, cellRenderer: (prop, item, index) => {
                    switch (prop) {
                        case 'likelihood':
                            return this.renderLikelihoodCell(item);
                        case 'severity':
                            return this.renderSeverityCell(item);
                        case 'indicative':
                            return this.renderIndicativeCell(item);
                        case 'source':
                            return this.renderSourceCell(item);
                        default:
                            return this.renderValueCell(item, mvvToRender[+prop]);
                    }
                }, tr: row => ({
                    className: _className(row && this.state.rowLoadingIds.includes(row._id ? row._id : 'newDV') ? 'loading' : undefined),
                    onContextMenu: e => {
                        if (row?._id)
                            this.onDiseaseValueRightClick(e, row);
                    }
                }) }) });
    }
    renderValueCell(dv, mvv) {
        let innerValueIds = [];
        const variable = ModuleFE_Variable.cache.unique(mvv.variableId);
        if (!variable)
            throw new MUSTNeverHappenException(`Could not find variable for id ${mvv.variableId}`);
        let dbValue = this.state.valuesMap[mvv.variableId];
        if (dbValue?.type === valueType_StandardRange) {
            dbValue = this.state.valuesMap[ValType_IND._id];
        }
        if (!dbValue)
            return '';
        switch (dbValue.type) {
            case valueType_Range:
                innerValueIds = dbValue.data.subsets;
                break;
            case valueType_SubTime:
            case valueType_SubEnumerated:
            case valueType_Enumerated:
                innerValueIds = dbValue.data.scope;
                break;
            case valueType_Tuple:
                innerValueIds = dbValue.data.scope;
                break;
            case valueType_Time:
                innerValueIds = dbValue.data.timeRanges;
                break;
        }
        if (variable.type === variableType_AttributeInstance)
            return this.renderValueCellForAttributeInstance(dv, mvv, variable, innerValueIds);
        return _jsx(DropDown_Values, { selected: dv.values[mvv.variableId], queryFilter: item => innerValueIds.includes(item._id), onSelected: value => this.onDiseaseValueSelected(dv, mvv, value?._id), variable: variable, renderer: value => _jsx("div", { className: 'node-data', children: resolveValueDisplay(value) }), canUnselect: true });
    }
    renderValueCellForAttributeInstance = (dv, mvv, variable, valueIds) => {
        const parentAttribute = ModuleFE_Variable.cache.unique(variable.parents.attributeId);
        if (!parentAttribute)
            throw new MUSTNeverHappenException(`Could not find variable for id ${variable.parents.attributeId}`);
        const parentScope = ModuleFE_Value.getRelatedValues(parentAttribute).values.map(dbObjectToId);
        const valueInScope = (value) => valueIds.includes(value._id);
        return _jsx(DropDown_Values, { selected: dv.values[mvv.variableId], queryFilter: item => parentScope.includes(item._id), onSelected: value => {
                if (value && !valueInScope(value))
                    return this.onDiseaseValueSelectedNotInScope(dv, mvv, variable, value);
                return this.onDiseaseValueSelected(dv, mvv, value?._id);
            }, variable: variable, renderer: value => {
                const className = _className('node-data', valueInScope(value) && 'in-scope');
                return _jsx("div", { className: className, children: resolveValueDisplay(value) });
            }, canUnselect: true, sortBy: [val => resolveValueDisplay(val), val => valueInScope(val) ? -1 : 1] });
    };
    renderLikelihoodCell(dv) {
        return _jsxs(LL_H_C, { className: 'likelihood-cell', children: [_jsx(QRangeInput, { value: dv.likelihoodRange, onValueBlur: val => this.onDiseaseLikelihoodRangeUpdated(dv, val), placeholder: 'Likelihood Range' }), _jsx(DropDown_Likelihood.selectable, { selected: dv.likelihood, onSelected: likelihood => this.onDiseaseLikelihoodSelected(dv, likelihood), canUnselect: true, placeholder: 'Likelihood' }), _jsx(DropDown_LikelihoodDirection.selectable, { selected: dv.likelihoodDirection, canUnselect: true, onSelected: dir => this.onDiseaseLikelihoodDirectionSelected(dv, dir), placeholder: 'Direction' })] });
    }
    renderSeverityCell(dv) {
        return _jsx(DropDown_Severity.selectable, { selected: dv.severityFactor, onSelected: severity => this.onDiseaseSeveritySelected(dv, severity), canUnselect: true, placeholder: 'Severity' });
    }
    renderIndicativeCell(dv) {
        const adapter = SimpleListAdapter([...indicativeOptions], item => _jsx("div", { className: 'node-data', children: item.item.label }));
        return _jsx(TS_DropDown, { adapter: adapter, selected: indicativeOptions.find(item => item.value === dv.indicative), onSelected: option => this.onIndicativeSelected(dv, option), canUnselect: true });
    }
    renderSourceCell(dv) {
        const modalityTagId = ConfigKeyFE_ModalityTagId.get();
        const tags = ModuleFE_Tag.cache.filter(tag => tag.tagIds.includes(modalityTagId));
        const adapter = SimpleListAdapter(tags, item => _jsx("div", { className: 'node-data', children: item.item.label }));
        const selected = tags.find(tag => tag._id === dv.sourceId);
        return _jsx(TS_DropDown, { adapter: adapter, selected: selected, onSelected: tag => this.onSourceTagSelected(dv, tag?._id), canUnselect: true });
    }
    // ######################### Render - References Tab #########################
    renderReferences() {
        return _jsxs(QScrollWrapper, { children: [_jsx(TS_Table, { header: [{ header: 'type', widthPx: 100 }, 'data'], headerRenderer: header => _jsx("div", { className: 'node-data', children: header }), rows: this.state.relatedReferences, cellRenderer: (prop, item, index) => {
                        switch (prop) {
                            case 'type':
                                return _jsx("div", { children: item.type });
                            case 'data':
                                if (item.type === 'book') {
                                    return _jsx("div", { children: resolveReferenceDisplay(item) });
                                }
                                return _jsx("div", { children: resolveReferenceDisplay(item) });
                            default:
                                return _jsx("div", { children: "ERROR" });
                        }
                    }, tr: (row) => ({
                        onContextMenu: (e) => this.onReferenceRightClick(e, row),
                    }) }), _jsx(HCSDropDowns.Reference, { queryFilter: ref => (this.state.diseaseProfile.referenceIds.includes(ref._id) && !this.state.multiVar.item.referenceIds.includes(ref._id)), renderer: item => {
                        if (item.type === 'book') {
                            return _jsx("div", { className: 'node-data', children: resolveReferenceDisplay(item) });
                        }
                        return _jsx("div", { className: 'node-data', children: resolveReferenceDisplay(item) });
                    }, selected: undefined, onSelected: this.onReferenceSelect })] });
    }
}
//# sourceMappingURL=Panel_DiseaseValueList.js.map